import { EQUIPMENT_COUNTERS_ENDPOINT } from "../../../../../../consts";
import { useCRUD } from "../../../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { EquipmentCounter } from "../../../../../../types/equipments";
import CustomCircularProgress from "../../../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../../../components/ui/breadcrumps/breadcrumbsContext";
import { formatCounterProvider, formatCounterValue, formatDateTime } from "../../../../../../utils";


export const EquipmentCounterDetail = () => {
  const { id, counterId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENT_COUNTERS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [counter, setCounter] = useState<EquipmentCounter | null>(null);

  useEffect(() => {
    const fetchCounter = async () => {
      if (counterId) {
        const response = await get(counterId);
        if (response.data) {
          setCounter(response.data);
          const counterLabel = `${response.data.kind_display} - ${formatDateTime(response.data.read_at)}`;
          setOverride(`/equipment-section/equipments/${id}/counters/${counterId}`, counterLabel);
        }
      }
    };

    const handleCounterUpdated = () => {
      fetchCounter();
    };

    window.addEventListener("equipmentCounterUpdated", handleCounterUpdated);
    fetchCounter();
    return () => {
      window.removeEventListener("equipmentCounterUpdated", handleCounterUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterId]);

  if (!counter) return <CustomCircularProgress />;

  const providedByName = counter.provided_by
    ? [counter.provided_by.first_name, counter.provided_by.last_name]
        .filter(Boolean)
        .join(" ") || counter.provided_by.email || "-"
    : "-";

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {counter.kind_display} - {formatDateTime(counter.read_at)}
        </Typography>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle1">
            <strong>{t("Kind")}:</strong> {counter.kind_display || counter.kind || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("Provider")}:</strong>{" "}
            {formatCounterProvider(
              counter.provider_display || counter.provider,
              counter.email_source_display,
            )}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("Value")}:</strong> {formatCounterValue(counter.value)}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("Read at")}:</strong> {counter.read_at ? formatDateTime(counter.read_at) : "-"}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Provided by Section */}
      {counter.provided_by && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Provided by")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="subtitle1">
                <strong>{t("Name and surname")}:</strong> {providedByName}
              </Typography>
              {counter.provided_by.email && (
                <Typography variant="subtitle1">
                  <strong>{t("Email")}:</strong> {counter.provided_by.email}
                </Typography>
              )}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(counter.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(counter.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
